import { Formik, FormikValues } from 'formik';
import React, { ChangeEvent, useContext, useState } from 'react';
import { ActivityIndicatorContext } from '../../../providers/ActivityIndicatorProvider/context';
import { Components } from './styled';
import ProjectTextField from '../../inputs/ProjectTextField';
import { ToastContext } from '../../../providers/ToastProvider/context';
import useModalState from '../../../hooks/useModalState';
import { isProjectError } from '../../../utils/errors';
import ProjectModal from '../../ProjectModal';
import ConfirmationModal from '../ConfirmationModal';
import { confirmationTexts } from '../ConfirmationModal/utils';
import { Pillar } from '../../../api/types/contentManagement';
import { updatePillar } from '../../../api/contentManagement';
import { uploadPublicFile } from '../../../utils/s3';
import { PUBLIC_BUCKET } from '../../../defines';
import { getFileExtension } from '../../../utils/s3/utils';
import { ReactComponent as AccessLinkIcon } from '../../../assets/icons/icon-grey-check-square-offset.svg';
import { getContentManagementUploadFileName } from '../../../api/contentManagement/utils';
import { getDisplayNames } from '../../../api/utils';
import { CreatePillarDto } from '../../../api/typeDtos/contentManagement';

const ModalBody = ({
  modalState,
  onSave,
  onCancel,
}: {
  modalState?: Pillar;
  onSave: (values: FormikValues) => Promise<void>;
  onCancel: () => void;
}) => {
  const { setIsLoading } = useContext(ActivityIndicatorContext);

  const [imagePath, setImagePath] = useState<string | undefined>(modalState?.imagePath || undefined);
  const [imageUrl, setImageUrl] = useState<string | undefined>();
  const [imageUploadProgress, setImageUploadProgress] = useState<number | undefined>(undefined);

  const _onSubmit = async (values: FormikValues) => {
    await onSave?.({
      ...values,
      imagePath: imagePath,
    });
  };

  const onUploadPublicFile = async (e: ChangeEvent<HTMLInputElement> | undefined, fileName: string, path: string) => {
    setIsLoading(true);
    const file = e?.target?.files?.[0];
    // @ts-ignore
    const newFile = new File([file], fileName, { type: file?.type });
    if (newFile) {
      if (e && e.target && e.target.value) {
        e.target.value = '';
      }

      await uploadPublicFile(newFile, path, (progress) => {
        setImageUploadProgress(progress);
        if (progress === 100) {
          setImagePath(path);
          const link = PUBLIC_BUCKET + path;
          setImageUrl(link);
          setImageUploadProgress(undefined);
        }
      });
    }
    setIsLoading(false);
  };

  const selectHandlerForImageFile = async (event: React.ChangeEvent<HTMLInputElement> | undefined) => {
    const file = event?.target?.files?.[0];

    if (file) {
      const extension = getFileExtension(file);
      const fileName = getContentManagementUploadFileName('pillar');
      const path = 'images/'.concat(fileName).concat('.', extension || '');
      await onUploadPublicFile(event, fileName, path);
    }
  };

  const onAccessFileLink = () => {
    const _imageUrl = imageUrl || PUBLIC_BUCKET + imagePath;
    window.open(_imageUrl, '_blank');
  };

  const onClearImageLink = () => {
    setImagePath(undefined);
  };

  return (
    <Formik initialValues={{}} onSubmit={_onSubmit}>
      {() => (
        <Components.Form>
          <Components.ModalBodyContainer>
            <Components.FieldsWrapper>
              <Components.FieldStartAdornment variant={'additional'} />
              <Components.FieldsContent>
                <Components.FieldWrapper>
                  <Components.FieldLabel>Image</Components.FieldLabel>
                  <Components.UploadFileWrapper>
                    <Components.InputMenu
                      type='file'
                      accept={'image/*'}
                      id='imageFile'
                      onChange={selectHandlerForImageFile}
                    />
                    <ProjectTextField value={imagePath || ''} disabled placeholder={'No image path'} />
                    {imagePath ? (
                      <>
                        <Components.IconWrapper onClick={() => onAccessFileLink()}>
                          <AccessLinkIcon />
                        </Components.IconWrapper>
                        <Components.ClearButton onClick={onClearImageLink}>Clear</Components.ClearButton>
                      </>
                    ) : (
                      <Components.UploadButton htmlFor={'imageFile'}>
                        {typeof imageUploadProgress === 'number' ? `${imageUploadProgress}%` : 'Upload Image'}
                      </Components.UploadButton>
                    )}
                  </Components.UploadFileWrapper>
                </Components.FieldWrapper>
              </Components.FieldsContent>
            </Components.FieldsWrapper>
          </Components.ModalBodyContainer>
          <Components.ModalButtonsContainer>
            <Components.ModalTertiaryButton onClick={() => onCancel?.()}>Cancel</Components.ModalTertiaryButton>
            <Components.ModalPrimaryButton type={'submit'}>Save</Components.ModalPrimaryButton>
          </Components.ModalButtonsContainer>
        </Components.Form>
      )}
    </Formik>
  );
};

const EditPillarModal = ({
  isModalOpen,
  onModalClose,
  modalState,
  refreshCallback,
}: {
  isModalOpen: boolean;
  onModalClose: (refreshTrigger?: boolean) => void;
  modalState?: { pillar: Pillar };
  refreshCallback?: () => Promise<void>;
}) => {
  const { setIsLoading } = useContext(ActivityIndicatorContext);
  const { showErrorToast, showSuccessToast } = useContext(ToastContext);

  const confirmationModal = useModalState<boolean>();

  const _onModalClose = (refreshTrigger?: boolean) => {
    confirmationModal.open(refreshTrigger || false);
  };

  const _onSave = async (values: FormikValues) => {
    const data: CreatePillarDto = {
      imagePath: values?.imagePath || null,
    };

    setIsLoading(true);
    const result = modalState?.pillar?.id
      ? await updatePillar({ ...data, id: modalState?.pillar?.id })
      : { success: false };
    setIsLoading(false);

    const resultError = isProjectError(result);
    if (resultError) {
      showErrorToast(resultError);
    } else {
      modalState && modalState?.pillar?.id
        ? showSuccessToast('The changes were saved successfully')
        : showSuccessToast('You successfully added a pillar');
      onModalClose?.(true);
      refreshCallback?.().then();
    }
  };

  const onConfirmCancel = (value: boolean) => {
    onModalClose?.(value);
  };

  return (
    <>
      <ProjectModal
        bodyChildren={<ModalBody modalState={modalState?.pillar} onSave={_onSave} onCancel={_onModalClose} />}
        isModalOpen={isModalOpen}
        onModalClose={_onModalClose}
        modalTitle={`Edit pillar "${getDisplayNames.pillarType(modalState?.pillar?.name) || undefined}"`}
        titleSuffix={modalState?.pillar?.id ? `(ID: ${modalState?.pillar?.id})` : undefined}
        disableCloseOnClickAway
      />
      <ConfirmationModal
        {...confirmationModal}
        confirmationText={confirmationTexts.lessons.cancelEditing}
        onConfirm={onConfirmCancel}
      />
    </>
  );
};

export default EditPillarModal;
